import { Center, Spinner, Stack, Text, VStack } from '@chakra-ui/react';
import {
  ContentTypes,
  IdQueryOperatorArgs,
  StringQueryOperatorArgs,
  UserContentView,
} from '@/client/services/api/graphql/gql/graphql';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import ErrorAlert from '@/client/components/data-display/ErrorAlert';
import { InView } from 'react-intersection-observer';
import LearnerDashboardNoDataPlaceholder from '@/client/components/learners/dashboard/components/LearnerDashboardNoDataPlaceholder';
import StackedLearnerDashboardContentListItem from '@/client/components/learners/dashboard/components/lists/StackedLearnerDashboardContentListItem';
import { graphQLClient } from '@/client/services/api/clients/graphqlClient';
import { listUserContentViewsQuery } from '@/client/components/learners/dashboard/api/queries/listUserContentViewsQuery';
import { useAuthStore } from '@/client/services/state/authStore';
import { useGetFormattedContentDuration } from '@/client/services/hooks/content/useGetContentDuration';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useLearnerDashboard } from '@/client/components/learners/dashboard/hooks/useLearnerDashboard';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// TODO: Consolidate handle route to content

export default function LearnerDashboardRecentlyViewed() {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const { searchTerm, selectedFilter, selectedDateFilter } = useOutletContext<{
    searchTerm: string;
    selectedFilter: { name: string; value: string | null };
    selectedDateFilter: {
      name: string;
      value: string | null;
      after: string | null;
      before: string | null;
    } | null;
  }>();
  const navigate = useNavigate();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { _id: userId } = user;
  const { getFormattedContentDuration } = useGetFormattedContentDuration();
  const { contentTypeTranslationMap } = useLearnerDashboard();

  const { data, isLoading, isError, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        'listUserContentViews',
        userId,
        selectedFilter?.value,
        searchTerm,
        selectedDateFilter?.after,
        selectedDateFilter?.before,
        pathname,
      ],
      queryFn: async ({ pageParam = 0 }) => {
        let payload: {
          user: IdQueryOperatorArgs;
          contentType?: StringQueryOperatorArgs;
          contentTitle?: StringQueryOperatorArgs;
          createdAt?: { after?: string; before?: string };
          page: number;
          pageSize: number;
        } = {
          user: { eq: userId },
          page: pageParam as number,
          pageSize: 50,
        };

        if (selectedFilter?.value) {
          payload = { ...payload, contentType: { eq: selectedFilter.value } };
        }

        if (searchTerm) {
          payload = { ...payload, contentTitle: { contains: searchTerm } };
        }

        if (pathname === '/dashboard/recently-viewed') {
          if (selectedDateFilter?.after || selectedDateFilter?.before) {
            payload.createdAt = {};
            if (selectedDateFilter.after) {
              payload.createdAt.after = selectedDateFilter.after;
            }
            if (selectedDateFilter.before) {
              payload.createdAt.before = selectedDateFilter.before;
            }
          }
        }

        const response = await graphQLClient.request(listUserContentViewsQuery, payload);

        return response.listUserContentViews;
      },
      getNextPageParam: (lastPage, pages) =>
        lastPage.pageInfo.hasNextPage ? pages.length : undefined,
    });

  const handleRouteToContent = (contentView: UserContentView) => {
    const { content } = contentView;

    const { contentType, id } = content;

    // Skip navigation if content ID is missing
    if (!id) return;

    const routeByContentTypeMap: Partial<Record<ContentTypes, string>> = {
      [ContentTypes.Article]: '/article',
      [ContentTypes.Video]: '/video',
      [ContentTypes.Scorm]: '/scorm',
      [ContentTypes.ImportedContent]: '/imported-content',
      [ContentTypes.File]: '/file',
    };

    navigate(`${routeByContentTypeMap[contentType]}/${id}`);
  };

  // Flatten all pages to get all content views
  const allContentViews =
    data?.pages.flatMap((page) => page.edges.map((edge) => edge.node as UserContentView)) || [];

  // Filter out duplicate content, keeping only the most recent view for each content item
  const uniqueContentViews = allContentViews.reduce((acc: UserContentView[], currentView) => {
    const existingIndex = acc.findIndex((view) => view.content.id === currentView.content.id);

    if (existingIndex === -1) {
      // Content not found, add it
      acc.push(currentView);
    } else {
      // Content already exists, keep the most recent view
      const existingView = acc[existingIndex];
      const existingDate = new Date(existingView.createdAt);
      const currentDate = new Date(currentView.createdAt);

      if (currentDate > existingDate) {
        // Replace with more recent view
        acc[existingIndex] = currentView;
      }
    }

    return acc;
  }, []);

  const searchIsActive = searchTerm || selectedFilter?.value || selectedDateFilter?.value;

  const searchIsInactive = !searchTerm && !selectedFilter?.value && !selectedDateFilter?.value;

  // Check if we have any data after filtering
  const hasData = uniqueContentViews.length > 0;

  // Simple debounce for fetchNextPage to prevent rapid triggers
  const [isDebouncing, setIsDebouncing] = useState(false);
  const handleFetchNextPage = () => {
    if (!isDebouncing && !isFetchingNextPage) {
      setIsDebouncing(true);

      fetchNextPage();
      setTimeout(() => setIsDebouncing(false), 1000); // 1 second debounce
    }
  };

  if (isError) {
    return (
      <ErrorAlert
        title={t('learners.dashboard.recentlyViewed.error')}
        backAction={{
          to: '/dashboard',
          label: t('learners.dashboard.recentlyViewed.error.backToDashboard'),
        }}
      />
    );
  }

  return isLoading ? (
    <Center marginTop={10}>
      <Spinner />
    </Center>
  ) : (
    <>
      {pathname === '/dashboard/recently-viewed' && (
        <Text fontSize="18px" fontWeight={600} color="baseBlack" marginBottom={4}>
          {selectedDateFilter?.name}
        </Text>
      )}

      {pathname !== '/dashboard/recently-viewed' && !hasData && (
        <LearnerDashboardNoDataPlaceholder
          imagePath="/public/images/Hiring-1--Streamline-Brooklyn.svg"
          title={t('learners.dashboard.recentlyViewed.noData.title')}
          helpText={t('learners.dashboard.levelUp')}
          buttonData={{
            buttonCopy: t('learners.dashboard.toExplore'),
            buttonAction: () => navigate('/explore'),
          }}
        />
      )}

      {pathname === '/dashboard/recently-viewed' && !hasData && (
        <LearnerDashboardNoDataPlaceholder
          imagePath="/public/images/Hiring-1--Streamline-Brooklyn.svg"
          title={t('learners.dashboard.recentlyViewed.noDataForDates.title', {
            date: selectedDateFilter?.name,
          })}
          helpText={t('learners.dashboard.recentlyViewed.noDataForDates.helpText')}
        />
      )}

      {hasData && (
        <VStack spacing={6} align="stretch" minHeight="80vh">
          <Stack spacing={0}>
            {uniqueContentViews.map((contentView) => {
              const { content, id } = contentView;

              return (
                <StackedLearnerDashboardContentListItem
                  key={id}
                  onClick={() => handleRouteToContent(contentView)}
                  id={id}
                  imageUrl={content.thumbnail || ''}
                  title={content.title || ''}
                  formattedContentDuration={getFormattedContentDuration(content.duration || 0)}
                  contentType={contentTypeTranslationMap[content.contentType as ContentTypes] || ''}
                />
              );
            })}
          </Stack>
        </VStack>
      )}

      {pathname === '/dashboard/recently-viewed' && hasNextPage && (
        <InView
          rootMargin="100px"
          as="div"
          threshold={0}
          onChange={(inView) => {
            if (inView) {
              handleFetchNextPage();
            }
          }}
        />
      )}
    </>
  );
}
